import dayjs from 'dayjs'
import { History } from 'lucide-react'

import { Button, Typography } from '~/components'
import { ErrorLogTerminal } from '~/components/common/ErrorLogTerminal'
import { CloseIcon, Modal, ModalFooter } from '~/components/modals/CurieModal'
import { ScheduledTask, useScheduledTask } from '~/services/scheduledTaskService'

const CRON_DAY_LABELS: Record<string, string> = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday'
}
const CRON_DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

function formatCronTime(hour: number, minute: number): string {
  const hour12 = hour % 12 || 12
  const ampm = hour < 12 ? 'AM' : 'PM'
  return minute === 0 ? `${hour12}${ampm}` : `${hour12}:${String(minute).padStart(2, '0')}${ampm}`
}

function expandCronDayPart(part: string): string[] {
  if (!part.includes('-')) return [CRON_DAY_LABELS[part] ?? part]
  const [start, end] = part.split('-')
  const startIndex = CRON_DAY_ORDER.indexOf(start)
  const endIndex = CRON_DAY_ORDER.indexOf(end)
  if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
    return [part]
  }
  return CRON_DAY_ORDER.slice(startIndex, endIndex + 1).map(day => CRON_DAY_LABELS[day])
}

function describeCronExpression(cron: string): string | null {
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return null
  const [minuteRaw, hourRaw, dayOfMonth, month, dayOfWeek] = parts
  const minute = Number(minuteRaw)
  const hour = Number(hourRaw)
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) return null
  if (minute < 0 || minute > 59 || hour < 0 || hour > 23) return null
  const time = formatCronTime(hour, minute)

  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return `Every day at ${time}`
  }
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === 'MON-FRI') {
    return `Weekdays at ${time}`
  }
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    const days = dayOfWeek.split(',').flatMap(expandCronDayPart).join(', ')
    return `Every ${days} at ${time}`
  }
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    return `Monthly on day ${dayOfMonth} at ${time}`
  }
  if (dayOfMonth !== '*' && month.startsWith('*/') && dayOfWeek === '*') {
    const monthInterval = Number(month.slice(2))
    if (!Number.isInteger(monthInterval) || monthInterval < 1) return null
    if (monthInterval === 1) {
      return `Monthly on day ${dayOfMonth} at ${time}`
    }
    return `Every ${monthInterval} months on day ${dayOfMonth} at ${time}`
  }
  return null
}

export function describeSchedule(s: ScheduledTask): string {
  switch (s.schedule_kind) {
    case 'ONCE':
      return s.run_at ? `Once on ${dayjs(s.run_at).format('MMM D, YYYY HH:mm')}` : 'Once'
    case 'INTERVAL': {
      if (!s.interval_seconds) return 'Interval'
      const secs = s.interval_seconds
      const WEEK = 7 * 24 * 3600
      const DAY = 24 * 3600
      if (secs % WEEK === 0) {
        const w = secs / WEEK
        return `Every ${w} week${w === 1 ? '' : 's'}`
      }
      if (secs % DAY === 0) {
        const d = secs / DAY
        return `Every ${d} day${d === 1 ? '' : 's'}`
      }
      if (secs % 3600 === 0) {
        const h = secs / 3600
        return `Every ${h} hour${h === 1 ? '' : 's'}`
      }
      if (secs % 60 !== 0) {
        return `Every ${secs} second${secs === 1 ? '' : 's'}`
      }
      const m = secs / 60
      return `Every ${m} minute${m === 1 ? '' : 's'}`
    }
    case 'CRON':
      if (!s.cron_expression) return 'Cron'
      return (
        describeCronExpression(s.cron_expression) ?? `cron: ${s.cron_expression} (${s.timezone})`
      )
    default:
      return s.schedule_kind
  }
}

/**
 * Modal showing the full stored prompt + runtime context for a single
 * schedule. Lazy-loads the detail (request_payload bytes are only decoded
 * server-side here, never in the list endpoint).
 */
export function ScheduledTaskDetailModal({
  uuid,
  isOpen,
  onClose
}: {
  uuid: string | null
  isOpen: boolean
  onClose: () => void
}) {
  const { data, isLoading, error } = useScheduledTask(uuid ?? undefined)

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='w-full max-w-[720px]' rawContent>
      <header className='flex min-h-16 w-full items-center justify-between border-b border-tokens-neutral-border-2 pl-6'>
        <div className='flex h-9 min-w-0 flex-1 items-center gap-4'>
          <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-tokens-neutral-icon-default/45'>
            <History className='h-[18px] w-[18px]' aria-hidden='true' />
          </div>
          <div className='flex min-w-0 flex-1 flex-col justify-center'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='medium'
              className='truncate select-none text-tokens-neutral-text-primary'
              style={{ lineHeight: '16px' }}
            >
              {data?.name ?? 'Scheduled Task'}
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='medium'
              className='truncate select-none text-tokens-neutral-text-secondary'
              style={{ lineHeight: '16px' }}
            >
              {data ? data.status : 'Loading details…'}
            </Typography>
          </div>
        </div>
        <button
          type='button'
          aria-label='Close modal'
          onClick={onClose}
          className='flex h-16 w-16 shrink-0 items-center justify-center text-tokens-neutral-icon-default transition-colors hover:bg-tokens-neutral-fill-fill-4'
        >
          <CloseIcon className='h-6 w-6' />
        </button>
      </header>

      <div className='min-h-[320px] max-h-[70vh] overflow-y-auto px-12 py-12'>
        {isLoading ? (
          <Typography fontFamily='geist' fontSize='bodyMd' className='select-none'>
            Loading…
          </Typography>
        ) : error ? (
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-status-error-text'
          >
            {`Failed to load: ${error.message}`}
          </Typography>
        ) : data ? (
          <div className='flex flex-col gap-4'>
            <DetailSection label='Prompt'>
              {data.description ? (
                <Typography fontFamily='geist' fontSize='bodySm'>
                  {data.description}
                </Typography>
              ) : (
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='select-none text-tokens-neutral-text-tertiary italic'
                >
                  No prompt provided.
                </Typography>
              )}
            </DetailSection>

            <DetailSection label='Schedule'>
              <DetailKV k='Schedule ID' v={data.uuid} />
              <DetailKV k='Type' v={data.cmd_type} />
              <DetailKV k='Schedule' v={describeSchedule(data)} />
              <DetailKV k='Next run (UTC)' v={data.next_run_time ?? '—'} />
              <DetailKV k='Last run (UTC)' v={data.last_run_time ?? '—'} />
              <DetailKV k='Successful runs' v={String(data.run_count)} />
              {data.failure_count ? (
                <DetailKV k='Consecutive failures' v={String(data.failure_count)} />
              ) : null}
              {data.last_error ? <DetailLog k='Last error' v={data.last_error} /> : null}
            </DetailSection>

            <DetailSection label='Recent runs'>
              {data.recent_runs.length === 0 ? (
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='select-none text-tokens-neutral-text-tertiary italic'
                >
                  No runs yet.
                </Typography>
              ) : (
                <div className='flex flex-col gap-1.5'>
                  {data.recent_runs.map(r => (
                    <div
                      key={r.task_id}
                      className='grid grid-cols-[1fr_1fr_1fr] items-center gap-3'
                    >
                      <Typography
                        fontFamily='geistMono'
                        fontSize='bodySm'
                        className='text-tokens-neutral-text-secondary'
                      >
                        #{r.task_id}
                      </Typography>
                      <Typography fontFamily='geist' fontSize='bodySm' className='text-center'>
                        {r.status}
                      </Typography>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        className='text-right text-tokens-neutral-text-tertiary'
                      >
                        {dayjs(r.created_time).format('MMM D, HH:mm')}
                      </Typography>
                    </div>
                  ))}
                </div>
              )}
            </DetailSection>
          </div>
        ) : null}
      </div>
      <ModalFooter>
        <Button variant='outlined' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

function DetailSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className='flex flex-col gap-2'>
      <Typography
        fontFamily='geist'
        fontSize='labelSm'
        fontWeight='semibold'
        className='select-none text-tokens-neutral-text-tertiary uppercase tracking-wide'
      >
        {label}
      </Typography>
      <div className='flex flex-col gap-1'>{children}</div>
    </section>
  )
}

function DetailKV({ k, v }: { k: string; v: string }) {
  return (
    <div className='flex items-baseline gap-3'>
      <Typography
        fontFamily='geist'
        fontSize='bodySm'
        className='min-w-[108px] select-none text-tokens-neutral-text-secondary'
      >
        {k}
      </Typography>
      <Typography fontFamily='geist' fontSize='bodySm' className='break-words'>
        {v}
      </Typography>
    </div>
  )
}

function DetailLog({ k, v }: { k: string; v: string }) {
  return (
    <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3'>
      <Typography
        fontFamily='geist'
        fontSize='bodySm'
        className='min-w-[108px] select-none text-tokens-neutral-text-secondary'
      >
        {k}
      </Typography>
      <ErrorLogTerminal value={v} className='flex-1' data-testid='scheduled-task-last-error-log' />
    </div>
  )
}
